import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Input,
  Progress,
} from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../../common/Loader";
import NoDataMessage from "../../../common/NoDataMessage";
import MenuBar from "../../../components/MenuBar";
import {
  ContentBatchRow,
  ContentEvalVote,
  ContentQuestionRow,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { ConfirmModal } from "../ConfirmModal";
import { QuestionEditorModal } from "../QuestionEditorModal";
import { useCategories } from "../useCategories";
import {
  effectiveGenerationStatus,
  generationFailureText,
  generationProgress,
  isRetryable,
} from "../batchStatus";
import { ReviewTable } from "./ReviewTable";

const POLL_INTERVAL_MS = 3_000;

/**
 * Slack's per-choice cap, which is the strictest of the three platforms and so
 * the one worth repairing against. Mirrors the value the Quality screen lints
 * with — keep the two in step.
 */
const MAX_CHOICE_LABEL_LENGTH = 29;

/** The admin server caps `limit` at 100; a batch can hold up to 200. */
const QUESTION_PAGE_SIZE = 100;

type Filter = "all" | "pending" | "failedQa";

const isPending = (question: ContentQuestionRow) =>
  !question.reviewStatus || question.reviewStatus === "pending";

const hasFailedQa = (question: ContentQuestionRow) =>
  (question.qa?.checks ?? []).some((check) => !check.pass);

const loadAllQuestions = async (
  batchId: string,
): Promise<ContentQuestionRow[]> => {
  const first = await Content.listQuestions({
    batchId,
    limit: QUESTION_PAGE_SIZE,
  });
  const all = [...(first.items ?? [])];

  while (all.length < first.total && (first.items ?? []).length > 0) {
    const next = await Content.listQuestions({
      batchId,
      limit: QUESTION_PAGE_SIZE,
      offset: all.length,
    });
    if (!next.items?.length) {
      break;
    }
    all.push(...next.items);
  }

  return all;
};

export const ContentBatchReviewPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const history = useHistory();
  const { categories } = useCategories();

  const [batch, setBatch] = useState<ContentBatchRow | null>(null);
  const [allBatches, setAllBatches] = useState<ContentBatchRow[]>([]);
  const [questions, setQuestions] = useState<ContentQuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [votes, setVotes] = useState<Record<string, ContentEvalVote>>({});
  const [editing, setEditing] = useState<ContentQuestionRow | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  /**
   * The repair rewrites the title and choices in place and the previous wording
   * is not kept anywhere, so it needs the same guard as publishing. It ran
   * straight from the button before, and "Fix all" can rewrite a whole set.
   */
  const [confirmFix, setConfirmFix] = useState<string[] | null>(null);
  /**
   * Reversing a decision is worth a pause: someone already judged this question
   * and a mis-click on a 200-row table would silently overturn them. Only a
   * change is confirmed — deciding a pending question is the normal path and
   * asking there would just be noise.
   */
  const [confirmReview, setConfirmReview] = useState<{
    questionIds: string[];
    reviewStatus: "approved" | "rejected";
    changing: number;
  } | null>(null);

  const latestLoad = useRef(0);
  useEffect(
    () => () => {
      latestLoad.current = -1;
    },
    [],
  );

  const load = useCallback(
    async (showSpinner: boolean) => {
      const loadId = ++latestLoad.current;
      const current = () => loadId === latestLoad.current;

      if (showSpinner) {
        setLoading(true);
      }

      // Sequenced, not Promise.all: a questions failure must not also hide the
      // batch header, which is where the generation status and Retry live — the
      // things you most need when something has gone wrong.
      try {
        const loadedBatch = await Content.getBatch(batchId);
        if (current()) {
          setBatch(loadedBatch);
        }
      } catch (error) {
        if (current()) {
          toast.error((error as Error).message || "Could not load the batch");
        }
        // Not guarded by current(): clearing the spinner is not a stale write.
        // A superseded load leaving it on wedges the page (see the finally).
        if (showSpinner) {
          setLoading(false);
        }
        return;
      }

      try {
        const items = await loadAllQuestions(batchId);
        if (current()) {
          setQuestions(items);
        }
      } catch (error) {
        if (current()) {
          toast.error(
            (error as Error).message || "Could not load the questions",
          );
        }
      } finally {
        // Deliberately not guarded by current(): the 3s poll starts as soon as
        // setBatch flips status to running, and its load(false) supersedes this
        // one. Skipping the clear here left the spinner on forever, because the
        // poll runs with showSpinner false and so never clears it either.
        if (showSpinner) {
          setLoading(false);
        }
      }
    },
    [batchId],
  );

  useEffect(() => {
    void load(true);
    setSelected(new Set());
    setExpanded(new Set());
  }, [load]);

  // Populates the batch switcher in the header.
  useEffect(() => {
    Content.listBatches({ state: "draft" })
      .then(({ items }) => setAllBatches(items ?? []))
      .catch(() => undefined);
  }, []);

  const status = effectiveGenerationStatus(batch?.generation);

  useEffect(() => {
    if (status !== "running") {
      return undefined;
    }
    const timer = setInterval(() => void load(false), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status, load]);

  const pendingCount = questions.filter(isPending).length;
  const failedQaCount = questions.filter(hasFailedQa).length;
  const fixable = questions.filter(hasFailedQa).map((question) => question.id);

  const visible = questions.filter((question) => {
    if (filter === "pending") return isPending(question);
    if (filter === "failedQa") return hasFailedQa(question);
    return true;
  });

  const run = async (
    label: string,
    work: () => Promise<void>,
    id: string | null = null,
  ) => {
    setBusyId(id);
    setWorking(true);
    try {
      await work();
      await load(false);
    } catch (error) {
      toast.error((error as Error).message || label);
    } finally {
      setBusyId(null);
      setWorking(false);
    }
  };

  const review = (
    questionIds: string[],
    reviewStatus: "approved" | "rejected",
  ) =>
    run(
      "Could not save the review",
      async () => {
        await Content.reviewBatch({ batchId, questionIds, reviewStatus });
        setSelected(new Set());
      },
      questionIds.length === 1 ? questionIds[0] : null,
    );

  /** Row toggles and the bulk buttons both come through here. */
  const requestReview = (
    questionIds: string[],
    reviewStatus: "approved" | "rejected",
  ) => {
    const reversing = questions.filter(
      (question) =>
        questionIds.includes(question.id) &&
        (question.reviewStatus === "approved" ||
          question.reviewStatus === "rejected") &&
        question.reviewStatus !== reviewStatus,
    );
    if (reversing.length > 0) {
      setConfirmReview({
        questionIds,
        reviewStatus,
        changing: reversing.length,
      });
      return;
    }
    void review(questionIds, reviewStatus);
  };

  const fixWithAi = (questionIds: string[]) =>
    run("Could not run the AI fix", async () => {
      const { fixedCount } = await Content.fixQaWithAi({
        questionIds,
        maxChoiceLabelLength: MAX_CHOICE_LABEL_LENGTH,
      });
      setSelected(new Set());
      // The platform discards a repair the rubric does not rate an improvement,
      // so say what actually changed rather than what was attempted.
      toast.success(
        fixedCount === 0
          ? "No question could be improved automatically — edit them by hand"
          : `Fixed ${fixedCount} of ${questionIds.length} question(s)`,
      );
    });

  const publish = () =>
    run("Could not publish", async () => {
      const { publishedCount } = await Content.publishBatch(batchId);
      toast.success(`Published ${publishedCount} question(s)`);
    });

  const vote = async (question: ContentQuestionRow, value: ContentEvalVote) => {
    // Optimistic: the vote tunes future generation rather than changing this
    // row, so waiting on the round trip would only make the click feel broken.
    setVotes((prev) => ({ ...prev, [question.id]: value }));
    try {
      await Content.upsertEval({ questionId: question.id, vote: value });
    } catch (error) {
      setVotes((prev) => {
        const next = { ...prev };
        delete next[question.id];
        return next;
      });
      toast.error((error as Error).message || "Could not record the feedback");
    }
  };

  const toggleSelected = (questionId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });

  const toggleAll = () => {
    const selectable = visible.filter(
      (question) => question.reviewStatus !== "approved",
    );
    const allSelected =
      selectable.length > 0 &&
      selectable.every((question) => selected.has(question.id));
    setSelected(allSelected ? new Set() : new Set(selectable.map((q) => q.id)));
  };

  const toggleExpanded = (questionId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });

  const commitRename = async (value: string) => {
    setRenaming(null);
    if (!value.trim() || value.trim() === batch?.name) {
      return;
    }
    await run("Could not rename", async () => {
      await Content.renameBatch({ batchId, name: value.trim() });
    });
  };

  if (loading) {
    return (
      <>
        <MenuBar />
        <Loader />
      </>
    );
  }

  if (!batch) {
    return (
      <>
        <MenuBar />
        <NoDataMessage light componentName="batch" />
      </>
    );
  }

  const progress = generationProgress(batch.generation);
  const failure = generationFailureText(batch.generation, status);
  const alreadyLive = batch.state === "live";
  const reviewedCount = batch.approvedCount + batch.rejectedCount;
  // Publish needs a decision on every question, not approval of every one —
  // matching the server, which refuses only while something is still pending.
  // Requiring approval throughout meant rejecting one weak question blocked the
  // other forty-nine, which is the opposite of what review is for. Rejected
  // questions are simply excluded from what goes live.
  const nothingPending =
    questions.length > 0 &&
    batch.approvedCount + batch.rejectedCount === questions.length;
  /** Nothing to activate — publishing would mark the set live and serve zero. */
  const nothingApproved = batch.approvedCount === 0;

  return (
    <>
      <MenuBar />
      <Card className="mb-3">
        <CardHeader>
          <div className="d-flex align-items-center flex-wrap">
            <Button
              color="link"
              className="p-0 mr-3"
              tag={Link}
              to="/content/drafts"
              title="Back to drafts"
            >
              <i className="fas fa-arrow-left" />
            </Button>
            <h2 className="h5 mb-0 mr-2">Review queue</h2>
            <Badge color={alreadyLive ? "success" : "secondary"}>
              {batch.state}
            </Badge>

            <div className="ml-auto d-flex align-items-center">
              <Input
                type="select"
                bsSize="sm"
                style={{ width: 240 }}
                className="mr-2"
                value={batchId}
                onChange={(e) =>
                  history.push(`/content/drafts/${e.target.value}`)
                }
              >
                {allBatches.every((b) => b.id !== batchId) && (
                  <option value={batchId}>{batch.name}</option>
                )}
                {allBatches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.state})
                  </option>
                ))}
              </Input>
              <Button
                size="sm"
                color="secondary"
                outline
                className="mr-2"
                onClick={() => history.push("/content/drafts")}
              >
                Back to drafts
              </Button>
              {isRetryable(status) && (
                <Button
                  size="sm"
                  color="warning"
                  outline
                  className="mr-2"
                  disabled={working}
                  onClick={() =>
                    run("Could not retry", async () => {
                      await Content.retryBatch(batchId);
                      toast.success("Retrying the remaining questions");
                    })
                  }
                >
                  Retry remaining
                </Button>
              )}
              <Button
                size="sm"
                color="primary"
                disabled={
                  working ||
                  alreadyLive ||
                  status === "running" ||
                  !nothingPending ||
                  nothingApproved
                }
                title={
                  alreadyLive
                    ? "Already published"
                    : status === "running"
                    ? "Generation is still running — review counts only cover what has been produced so far"
                    : questions.length === 0
                    ? "No questions loaded for this set"
                    : !nothingPending
                    ? `${
                        questions.length -
                        (batch.approvedCount + batch.rejectedCount)
                      } question(s) still need a decision`
                    : nothingApproved
                    ? "Every question was rejected, so there is nothing to publish"
                    : "Publish now"
                }
                onClick={() => setConfirmPublish(true)}
              >
                <i className="fas fa-rocket mr-1" />
                Publish now
              </Button>
            </div>
          </div>

          <div className="d-flex align-items-center mt-2 small">
            <span className="mr-1">Batch:</span>
            {renaming !== null ? (
              <Input
                autoFocus
                bsSize="sm"
                style={{ width: 260 }}
                value={renaming}
                onChange={(e) => setRenaming(e.target.value)}
                onBlur={() => void commitRename(renaming)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void commitRename(renaming);
                  if (e.key === "Escape") setRenaming(null);
                }}
              />
            ) : (
              <>
                <strong>{batch.name}</strong>
                <Button
                  color="link"
                  className="p-0 ml-2"
                  title="Rename batch"
                  onClick={() => setRenaming(batch.name)}
                >
                  <i className="fas fa-pen small" />
                </Button>
              </>
            )}
          </div>

          {/* The ask, next to what came back. A batch generated from "World
              Cup Final - July 2026" returned twenty correct questions about
              earlier tournaments, and nothing on this screen showed what had
              been requested — so the drift was invisible until someone read
              all twenty. Nothing checks relevance, which makes showing the
              prompt the cheapest way to make it checkable. */}
          {batch.generation?.prompt ? (
            <div className="d-flex mt-1 small text-muted">
              <span className="mr-1 flex-shrink-0">Asked for:</span>
              <span className="font-italic">{batch.generation.prompt}</span>
            </div>
          ) : null}

          <div className="small text-muted mt-1">
            {reviewedCount} of {questions.length} reviewed ·{" "}
            {batch.approvedCount} approved · {batch.rejectedCount} rejected ·{" "}
            {failedQaCount === 0 ? (
              "all passed QA"
            ) : (
              <span className="text-danger">{failedQaCount} failed QA</span>
            )}
          </div>
        </CardHeader>

        <CardBody className="py-2">
          <div className="d-flex align-items-center flex-wrap">
            <span className="small text-muted mr-2">
              <i className="fas fa-info-circle mr-1" />
              AI-generated — assumed OK; spot-check and bulk-approve, flag the
              exceptions.
            </span>
            {/* A published set is a record — the repair rewrites content in
                place, which is exactly what must not be on offer once live. */}
            {fixable.length > 0 && !alreadyLive && (
              <Button
                size="sm"
                color="primary"
                className="mr-2"
                style={{ backgroundColor: "#5b3ecc", borderColor: "#5b3ecc" }}
                disabled={working}
                onClick={() => setConfirmFix(fixable)}
              >
                <i className="fas fa-magic mr-1" />
                Fix all {fixable.length} fixable with AI
              </Button>
            )}

            <ButtonGroup size="sm" className="ml-auto">
              <Button
                color={filter === "all" ? "secondary" : "light"}
                onClick={() => setFilter("all")}
              >
                All ({questions.length})
              </Button>
              <Button
                color={filter === "pending" ? "secondary" : "light"}
                onClick={() => setFilter("pending")}
              >
                Pending ({pendingCount})
              </Button>
              <Button
                color={filter === "failedQa" ? "danger" : "light"}
                className={failedQaCount > 0 ? "text-danger" : ""}
                onClick={() => setFilter("failedQa")}
              >
                Failed QA ({failedQaCount})
              </Button>
            </ButtonGroup>
          </div>

          {batch.generation && (
            <>
              <div className="d-flex align-items-center small mt-2">
                <Badge
                  color={
                    status === "done"
                      ? "success"
                      : status === "running"
                      ? "info"
                      : "danger"
                  }
                  className="mr-2"
                >
                  {status === "running" && (
                    <i className="fas fa-circle-notch fa-spin mr-1" />
                  )}
                  {status}
                </Badge>
                <span className="mr-2">
                  {progress.produced}/{progress.requested} produced ·{" "}
                  {batch.generation.qaPassed} QA-clean
                </span>
                <span className="text-muted">{batch.generation.prompt}</span>
              </div>
              <Progress
                className="mt-2"
                style={{ height: 4 }}
                value={progress.percent}
                color={
                  status === "done"
                    ? "success"
                    : status === "running"
                    ? "info"
                    : "danger"
                }
              />
              {failure && (
                <div className="small text-danger mt-2">{failure}</div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {pendingCount === 0 && questions.length > 0 && !alreadyLive && (
        <Alert color="info" className="py-2">
          All questions reviewed. Every decision is already saved — publish when
          you are ready.
        </Alert>
      )}

      {/* A published set is a record. Selecting rows there would surface bulk
          approve, reject and rewrite, all of which would change what already
          shipped, so the whole bar goes away rather than being disabled. */}
      {selected.size > 0 && !alreadyLive && (
        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center flex-wrap">
            <span className="mr-3">
              <i className="fas fa-check-square mr-1" />
              {selected.size} selected
            </span>
            <Button
              size="sm"
              color="success"
              className="mr-2"
              disabled={working}
              onClick={() => requestReview(Array.from(selected), "approved")}
            >
              <i className="fas fa-check mr-1" />
              Approve selected
            </Button>
            <Button
              size="sm"
              className="mr-2 text-white"
              style={{ backgroundColor: "#5b3ecc", borderColor: "#5b3ecc" }}
              disabled={working}
              onClick={() => setConfirmFix(Array.from(selected))}
            >
              <i className="fas fa-magic mr-1" />
              Fix selected with AI
            </Button>
            <Button
              size="sm"
              color="danger"
              outline
              className="mr-2"
              disabled={working}
              onClick={() => requestReview(Array.from(selected), "rejected")}
            >
              <i className="fas fa-times mr-1" />
              Reject selected
            </Button>
            <Button
              size="sm"
              color="link"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
          </CardBody>
        </Card>
      )}

      <Card>
        {visible.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {status === "running"
              ? "Generating — questions appear here as they are produced."
              : questions.length === 0
              ? "This batch has no questions."
              : "Nothing matches this filter."}
          </div>
        ) : (
          <ReviewTable
            questions={visible}
            categories={categories}
            selected={selected}
            expanded={expanded}
            busyId={busyId}
            votes={votes}
            prompt={batch.generation?.prompt}
            onToggleSelected={toggleSelected}
            onToggleAll={toggleAll}
            onToggleExpanded={toggleExpanded}
            onReview={requestReview}
            readOnly={alreadyLive}
            onEdit={setEditing}
            onVote={vote}
          />
        )}
      </Card>

      {editing && (
        <QuestionEditorModal
          question={editing}
          categories={categories}
          isOpen
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            // Re-read rather than patching in place: an edit can clear a QA
            // failure, and the chips come from the server's rubric run.
            void load(false);
          }}
        />
      )}

      {confirmFix && (
        <ConfirmModal
          isOpen
          busy={working}
          title="Let the model rewrite these?"
          confirmLabel={`Fix ${confirmFix.length}`}
          confirmColor="primary"
          onCancel={() => setConfirmFix(null)}
          onConfirm={() => {
            const ids = confirmFix;
            setConfirmFix(null);
            void fixWithAi(ids);
          }}
        >
          <p className="mb-2">
            <strong>
              {confirmFix.length} question{confirmFix.length === 1 ? "" : "s"}
            </strong>{" "}
            will have their wording and answers rewritten in place.
          </p>
          <p className="mb-0 text-muted">
            The current wording is not kept, so this cannot be undone from here.
            A rewrite the quality checks do not rate as an improvement is
            discarded, so some questions may come back unchanged.
          </p>
        </ConfirmModal>
      )}

      {confirmReview && (
        <ConfirmModal
          isOpen
          busy={working}
          title="Change a decision already made?"
          confirmLabel={
            confirmReview.reviewStatus === "approved"
              ? "Approve instead"
              : "Reject instead"
          }
          confirmColor={
            confirmReview.reviewStatus === "approved" ? "success" : "danger"
          }
          onCancel={() => setConfirmReview(null)}
          onConfirm={() => {
            const { questionIds, reviewStatus } = confirmReview;
            setConfirmReview(null);
            void review(questionIds, reviewStatus);
          }}
        >
          <p className="mb-0">
            {confirmReview.changing === 1
              ? "One question has already been "
              : `${confirmReview.changing} questions have already been `}
            {confirmReview.reviewStatus === "approved"
              ? "rejected"
              : "approved"}
            . Changing {confirmReview.changing === 1 ? "it" : "them"} to{" "}
            <strong>{confirmReview.reviewStatus}</strong> overturns that
            decision.
          </p>
        </ConfirmModal>
      )}

      {confirmPublish && (
        <ConfirmModal
          isOpen
          title="Publish batch?"
          confirmLabel="Publish"
          confirmColor="primary"
          busy={working}
          onCancel={() => setConfirmPublish(false)}
          onConfirm={() => {
            setConfirmPublish(false);
            void publish();
          }}
        >
          Publish <strong>“{batch.name}”</strong>? {batch.approvedCount}{" "}
          approved question{batch.approvedCount === 1 ? "" : "s"} will go live
          (state <Badge color="success">active</Badge>) and appear in the
          library.
          {batch.rejectedCount > 0 ? (
            <>
              {" "}
              <strong>
                {batch.rejectedCount} rejected question
                {batch.rejectedCount === 1 ? "" : "s"} will be retired at the
                same time
              </strong>{" "}
              — removed from the bank (state{" "}
              <Badge color="secondary">deleted</Badge>) and not usable by anyone
              afterwards. This cannot be undone from here.
            </>
          ) : null}
        </ConfirmModal>
      )}
    </>
  );
};
